import React, { useCallback, useEffect, useRef, useState } from 'react';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';
import { SECOND_BRAIN_QUESTION_MIN_LENGTH, SECOND_BRAIN_QUESTION_MAX_LENGTH } from '@/features/secondBrain/config';

import { useSecondBrainTags, normalizeTag } from './useSecondBrainTags';
import { useSecondBrainHistory } from './useSecondBrainHistory';
import { useSecondBrainChat } from './useSecondBrainChat';
import { useSecondBrainNotes } from './useSecondBrainNotes';

const useSecondBrainExperience = () => {
  // UI state - command-palette style history (⌘K)
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const contextRequest = useRef<AbortController | null>(null);
  const showAlert = useAlertStore((state) => state.showAlert);

  // Tags hook
  const { availableTags, selectedTags, isLoadingTags, setSelectedTags, clearSelectedTags } = useSecondBrainTags();

  // History hook
  const {
    chatHistory,
    activeChatId,
    activeChat,
    isLoadingHistory,
    setActiveChatId,
    setChatHistory,
    deleteChat,
    clearActiveChat,
  } = useSecondBrainHistory();

  // Chat hook
  const { question, isStreaming, setQuestion, startChat, cancelChat, clearQuestion } = useSecondBrainChat({
    setChatHistory,
    setActiveChatId,
  });

  // Notes hook
  const {
    contextNotesPreview,
    displayedContextNotes,
    isLoadingPreview,
    isFetchingContextNotes,
    setIsFetchingContextNotes,
    setContextNotesPreview,
    fetchContextNotes,
    clearContextNotesPreview,
  } = useSecondBrainNotes(selectedTags, activeChat, isStreaming);

  // Handlers
  const handleNewChat = useCallback(() => {
    contextRequest.current?.abort();
    contextRequest.current = null;
    setIsFetchingContextNotes(false);
    cancelChat();
    clearActiveChat();
    clearQuestion();
    clearSelectedTags();
    clearContextNotesPreview();
    setIsPaletteOpen(false);
  }, [
    cancelChat,
    clearActiveChat,
    clearQuestion,
    clearSelectedTags,
    clearContextNotesPreview,
    setIsFetchingContextNotes,
  ]);

  const handleDeleteChat = useCallback(
    async (chatId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      await deleteChat(chatId);
    },
    [deleteChat],
  );

  const handleStartChat = useCallback(async () => {
    if (contextRequest.current || isStreaming) return;
    const trimmedQuestion = question.trim();
    if (
      trimmedQuestion.length < SECOND_BRAIN_QUESTION_MIN_LENGTH ||
      trimmedQuestion.length > SECOND_BRAIN_QUESTION_MAX_LENGTH
    ) {
      showAlert(
        `Ask a question between ${SECOND_BRAIN_QUESTION_MIN_LENGTH} and ${SECOND_BRAIN_QUESTION_MAX_LENGTH} characters.`,
        'warning',
      );
      return;
    }
    const controller = new AbortController();
    contextRequest.current = controller;
    setIsFetchingContextNotes(true);
    try {
      const notes = await fetchContextNotes(selectedTags, trimmedQuestion, controller.signal);
      if (controller.signal.aborted) return;
      setContextNotesPreview(notes);
      await startChat(selectedTags, notes);
    } catch (error) {
      if (controller.signal.aborted) return;
      errorReporter.expected('Failed to find Second Brain context notes', error, {
        feature: 'secondBrain',
        operation: 'fetchContextNotes',
      });
      showAlert('We could not search your notes. Please try again.', 'error');
    } finally {
      if (contextRequest.current === controller) {
        contextRequest.current = null;
        setIsFetchingContextNotes(false);
      }
    }
  }, [
    fetchContextNotes,
    selectedTags,
    question,
    isStreaming,
    showAlert,
    setContextNotesPreview,
    setIsFetchingContextNotes,
    startChat,
  ]);

  useEffect(() => () => contextRequest.current?.abort(), []);

  const handleSelectChat = useCallback(
    (chatId: string) => {
      contextRequest.current?.abort();
      contextRequest.current = null;
      setIsFetchingContextNotes(false);
      // Restore the chat's tag scope so the follow-up composer stays usable.
      // Normalize first: availableTags is lowercased/trimmed by sortTags, but
      // chat.tags comes back from the API with its original casing, so an
      // un-normalized "Marketing" never matches the "marketing" in the picker —
      // it renders unselected, and selecting it appends a duplicate.
      const chat = chatHistory.find((item) => item.id === chatId);
      if (chat) {
        setSelectedTags([...new Set(chat.tags.map(normalizeTag))].filter(Boolean));
      }
      setActiveChatId(chatId);
      setIsPaletteOpen(false);
    },
    [chatHistory, setSelectedTags, setActiveChatId, setIsFetchingContextNotes],
  );

  const handleOpenPalette = useCallback(() => {
    setIsPaletteOpen(true);
  }, []);

  const handleClosePalette = useCallback(() => {
    setIsPaletteOpen(false);
  }, []);

  // ⌘K / Ctrl+K toggles the history palette
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isPaletteOpen,
    availableTags,
    selectedTags,
    isLoadingTags,
    chatHistory,
    activeChatId,
    activeChat,
    isLoadingHistory,
    question,
    isStreaming,
    contextNotesPreview,
    displayedContextNotes,
    isLoadingPreview,
    isFetchingContextNotes,
    onSelectChat: handleSelectChat,
    onTagsChange: setSelectedTags,
    onQuestionChange: setQuestion,
    onNewChat: handleNewChat,
    onDeleteChat: handleDeleteChat,
    onStartChat: handleStartChat,
    onCancelChat: cancelChat,
    onOpenPalette: handleOpenPalette,
    onClosePalette: handleClosePalette,
  };
};

export type SecondBrainExperienceState = ReturnType<typeof useSecondBrainExperience>;

export default useSecondBrainExperience;
